import { useEffect, useMemo, useState } from "react";
import { Check, Clipboard, Download, Loader2, Plus, RefreshCw, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, Panel, Overline } from "@/components/Panel";
import {
  createAdminCustomer,
  getAdminTenantFields,
  getAdminTenants,
  onboardAdminDevice,
  retryAdminDeviceCredential,
} from "@/api/treeplet";

const inputClass = "w-full h-10 rounded-md border border-line bg-surface px-3 text-sm outline-none focus:border-brand";
const labelClass = "block text-xs font-semibold uppercase tracking-wider text-ink-secondary mb-1";

function generatedPassword() {
  const bytes = new Uint8Array(24);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, (value) => value.toString(36).padStart(2, "0")).join("").slice(0, 32);
}

function errorMessage(error) {
  if (typeof error?.detail === "string") return error.detail;
  if (error?.detail?.message) return error.detail.message;
  return error?.message || "No se pudo completar el onboarding";
}

function DeliveryField({ label, value, className = "", mono = false }) {
  if (value === null || value === undefined || value === "") return null;
  return (
    <div className={`min-w-0 ${className}`}>
      <dt className="text-[9px] font-semibold uppercase tracking-wider text-ink-tertiary">{label}</dt>
      <dd className={`mt-0.5 text-[11px] leading-[15px] text-ink break-all ${mono ? "font-mono" : ""}`}>{String(value)}</dd>
    </div>
  );
}

export default function AdminOnboarding() {
  const [stage, setStage] = useState("customer");
  const [mode, setMode] = useState("new");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [tenants, setTenants] = useState([]);
  const [fields, setFields] = useState([]);
  const [selectedTenantId, setSelectedTenantId] = useState("");
  const [selectedFieldId, setSelectedFieldId] = useState("");
  const [context, setContext] = useState(null);
  const [result, setResult] = useState(null);
  const [failedDeviceId, setFailedDeviceId] = useState("");
  const [customer, setCustomer] = useState({
    customerName: "", displayName: "", email: "", password: generatedPassword(),
    fieldName: "", latitude: "", longitude: "",
  });
  const [device, setDevice] = useState({
    serialNumber: "", deviceModel: "treeplet-sentinel-v1", sensorType: "cwt-leaf-th-s-n",
    firmwareVersion: "0.1.0", latitude: "", longitude: "",
  });

  useEffect(() => {
    getAdminTenants()
      .then((envelope) => setTenants(envelope.data || []))
      .catch((requestError) => setError(errorMessage(requestError)));
  }, []);

  useEffect(() => {
    if (mode !== "existing" || !selectedTenantId) {
      setFields([]);
      setSelectedFieldId("");
      return;
    }
    getAdminTenantFields(selectedTenantId)
      .then((envelope) => setFields(envelope.data || []))
      .catch((requestError) => setError(errorMessage(requestError)));
  }, [mode, selectedTenantId]);

  const selectedTenant = useMemo(
    () => tenants.find((tenant) => tenant.id === selectedTenantId),
    [tenants, selectedTenantId],
  );
  const selectedField = useMemo(
    () => fields.find((field) => field.id === selectedFieldId),
    [fields, selectedFieldId],
  );

  const updateCustomer = (event) => setCustomer((current) => ({ ...current, [event.target.name]: event.target.value }));
  const updateDevice = (event) => setDevice((current) => ({ ...current, [event.target.name]: event.target.value }));

  const continueToDevice = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      let nextContext;
      if (mode === "new") {
        const created = await createAdminCustomer({
          customer_name: customer.customerName,
          user: { email: customer.email, display_name: customer.displayName, password: customer.password },
          initial_field: {
            name: customer.fieldName,
            latitude: Number(customer.latitude),
            longitude: Number(customer.longitude),
          },
        });
        nextContext = {
          tenant: created.tenant,
          field: created.field,
          account: { email: created.user.email, initial_password: customer.password },
        };
      } else {
        if (!selectedTenant || !selectedField) throw new Error("Selecciona un cliente y una parcela");
        nextContext = { tenant: selectedTenant, field: selectedField, account: null };
      }
      setContext(nextContext);
      setDevice((current) => ({
        ...current,
        latitude: String(nextContext.field.latitude ?? ""),
        longitude: String(nextContext.field.longitude ?? ""),
      }));
      setStage("device");
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setBusy(false);
    }
  };

  const submitDevice = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    setFailedDeviceId("");
    try {
      const onboarded = await onboardAdminDevice({
        tenant_id: context.tenant.id,
        field_id: context.field.id,
        serial_number: device.serialNumber,
        device_model: device.deviceModel,
        sensor_type: device.sensorType,
        firmware_version: device.firmwareVersion || null,
        latitude: Number(device.latitude),
        longitude: Number(device.longitude),
      });
      setResult(onboarded);
      setStage("result");
    } catch (requestError) {
      setError(errorMessage(requestError));
      setFailedDeviceId(requestError?.detail?.device_id || "");
    } finally {
      setBusy(false);
    }
  };

  const retryProvisioning = async () => {
    setBusy(true);
    setError("");
    try {
      const onboarded = await retryAdminDeviceCredential(failedDeviceId);
      setResult(onboarded);
      setStage("result");
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setBusy(false);
    }
  };

  const handoff = useMemo(() => result ? {
    generated_at: new Date().toISOString(),
    desktop: { url: window.location.origin, ...(context.account || {}) },
    customer: { tenant_id: context.tenant.id, tenant_name: context.tenant.name, field_id: context.field.id, field_name: context.field.name },
    device: result.device,
    mqtt: result.mqtt,
  } : null, [context, result]);

  const copyHandoff = async () => {
    await navigator.clipboard.writeText(JSON.stringify(handoff, null, 2));
    toast.success("Configuración copiada");
  };

  const downloadHandoff = () => {
    const url = URL.createObjectURL(new Blob([JSON.stringify(handoff, null, 2)], { type: "application/json" }));
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `treeplet-${result.device.id}-onboarding.json`;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const reset = () => {
    setStage("customer");
    setMode("new");
    setContext(null);
    setResult(null);
    setFailedDeviceId("");
    setError("");
    setCustomer({ customerName: "", displayName: "", email: "", password: generatedPassword(), fieldName: "", latitude: "", longitude: "" });
    setDevice({ serialNumber: "", deviceModel: "treeplet-sentinel-v1", sensorType: "cwt-leaf-th-s-n", firmwareVersion: "0.1.0", latitude: "", longitude: "" });
  };

  return (
    <div className="h-full min-h-0 overflow-y-auto lg:overflow-hidden max-w-[1100px] mx-auto w-full flex flex-col">
      <PageHeader title="Onboarding" subtitle="Alta de clientes y dispositivos sin acceso al VPS" />
      <div className="flex gap-2 mb-4 text-sm shrink-0">
        {["customer", "device", "result"].map((item, index) => (
          <div key={item} className={`flex-1 rounded-md px-3 py-2 border ${stage === item ? "border-brand bg-brand/5 text-brand" : "border-line text-ink-tertiary"}`}>
            {index + 1}. {item === "customer" ? "Cliente" : item === "device" ? "Dispositivo" : "Entrega"}
          </div>
        ))}
      </div>

      {error && <div className="mb-4 rounded-md border border-critical/30 bg-critical/5 px-4 py-3 text-sm text-critical">{error}</div>}

      {stage === "customer" && (
        <Panel className="p-4 lg:p-5 min-h-0">
          <Overline>Cliente y parcela</Overline>
          <div className="flex gap-2 my-3">
            <button type="button" onClick={() => setMode("new")} className={`px-4 py-2 rounded-md text-sm font-semibold ${mode === "new" ? "bg-brand text-white" : "bg-page text-ink-secondary"}`}>Nuevo cliente</button>
            <button type="button" onClick={() => setMode("existing")} className={`px-4 py-2 rounded-md text-sm font-semibold ${mode === "existing" ? "bg-brand text-white" : "bg-page text-ink-secondary"}`}>Cliente existente</button>
          </div>
          <form onSubmit={continueToDevice} className="grid md:grid-cols-6 gap-x-4 gap-y-3">
            {mode === "new" ? <>
              <label className="md:col-span-3"><span className={labelClass}>Empresa o cliente</span><input required className={inputClass} name="customerName" value={customer.customerName} onChange={updateCustomer} /></label>
              <label className="md:col-span-3"><span className={labelClass}>Nombre visible</span><input required className={inputClass} name="displayName" value={customer.displayName} onChange={updateCustomer} /></label>
              <label className="md:col-span-3"><span className={labelClass}>Correo de acceso</span><input required type="email" className={inputClass} name="email" value={customer.email} onChange={updateCustomer} /></label>
              <label className="md:col-span-3"><span className={labelClass}>Contraseña inicial</span><div className="flex gap-2"><input required minLength={16} className={inputClass} name="password" value={customer.password} onChange={updateCustomer} /><button type="button" title="Generar otra" onClick={() => setCustomer((current) => ({ ...current, password: generatedPassword() }))} className="px-3 rounded-md border border-line"><RefreshCw size={16} /></button></div></label>
              <label className="md:col-span-2"><span className={labelClass}>Parcela inicial</span><input required className={inputClass} name="fieldName" value={customer.fieldName} onChange={updateCustomer} /></label>
              <label className="md:col-span-2"><span className={labelClass}>Latitud</span><input required type="number" step="any" min="-90" max="90" className={inputClass} name="latitude" value={customer.latitude} onChange={updateCustomer} /></label>
              <label className="md:col-span-2"><span className={labelClass}>Longitud</span><input required type="number" step="any" min="-180" max="180" className={inputClass} name="longitude" value={customer.longitude} onChange={updateCustomer} /></label>
            </> : <>
              <label className="md:col-span-3"><span className={labelClass}>Cliente</span><select required className={inputClass} value={selectedTenantId} onChange={(event) => setSelectedTenantId(event.target.value)}><option value="">Selecciona un cliente</option>{tenants.map((tenant) => <option key={tenant.id} value={tenant.id}>{tenant.name}</option>)}</select></label>
              <label className="md:col-span-3"><span className={labelClass}>Parcela</span><select required className={inputClass} value={selectedFieldId} onChange={(event) => setSelectedFieldId(event.target.value)} disabled={!selectedTenantId}><option value="">Selecciona una parcela</option>{fields.map((field) => <option key={field.id} value={field.id}>{field.name}</option>)}</select></label>
            </>}
            <div className="md:col-span-6 flex justify-end pt-1"><button disabled={busy} className="h-10 px-5 rounded-md bg-brand text-white font-semibold inline-flex items-center gap-2">{busy && <Loader2 size={16} className="animate-spin" />}Continuar</button></div>
          </form>
        </Panel>
      )}

      {stage === "device" && (
        <Panel className="p-4 lg:p-5 min-h-0">
          <Overline>{context.tenant.name} · {context.field.name}</Overline>
          <h2 className="text-xl font-bold mt-1.5 mb-4">Asignar dispositivo</h2>
          <form onSubmit={submitDevice} className="grid md:grid-cols-2 gap-x-4 gap-y-3">
            <label><span className={labelClass}>Número de serie</span><input required className={inputClass} name="serialNumber" value={device.serialNumber} onChange={updateDevice} /></label>
            <label><span className={labelClass}>Firmware</span><input className={inputClass} name="firmwareVersion" value={device.firmwareVersion} onChange={updateDevice} /></label>
            <label><span className={labelClass}>Modelo</span><input required className={inputClass} name="deviceModel" value={device.deviceModel} onChange={updateDevice} /></label>
            <label><span className={labelClass}>Sensores</span><input readOnly className={`${inputClass} bg-page`} value={`${device.sensorType} + sht3xd`} /></label>
            <label><span className={labelClass}>Latitud</span><input required type="number" step="any" min="-90" max="90" className={inputClass} name="latitude" value={device.latitude} onChange={updateDevice} /></label>
            <label><span className={labelClass}>Longitud</span><input required type="number" step="any" min="-180" max="180" className={inputClass} name="longitude" value={device.longitude} onChange={updateDevice} /></label>
            <div className="md:col-span-2 flex justify-between pt-1">{mode === "existing" ? <button type="button" onClick={() => setStage("customer")} className="h-10 px-5 rounded-md border border-line font-semibold">Volver</button> : <span className="text-xs text-ink-tertiary self-center">El cliente ya está creado; completa o reintenta este dispositivo.</span>}<button disabled={busy} className="h-10 px-5 rounded-md bg-brand text-white font-semibold inline-flex items-center gap-2">{busy && <Loader2 size={16} className="animate-spin" />}Crear y provisionar</button></div>
          </form>
          {failedDeviceId && <div className="mt-5 rounded-md bg-moderate/10 p-4"><p className="text-sm mb-3">El dispositivo se creó como <code>{failedDeviceId}</code>. Puedes reintentar sin duplicarlo.</p><button disabled={busy} onClick={retryProvisioning} className="px-4 py-2 rounded-md bg-brand text-white text-sm font-semibold">Reintentar credencial MQTT</button></div>}
        </Panel>
      )}

      {stage === "result" && handoff && (
        <Panel className="p-4 min-h-0 overflow-hidden">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3"><div className="rounded-full bg-stable/10 text-stable p-2"><Check size={20} /></div><div><Overline>Onboarding completado</Overline><h2 className="text-lg font-bold mt-0.5">Credenciales listas para entregar</h2></div></div>
            <span className="text-[10px] text-ink-tertiary whitespace-nowrap">{new Date(handoff.generated_at).toLocaleString("es-ES")}</span>
          </div>
          <div className="mt-3 rounded-md border border-moderate/30 bg-moderate/5 px-3 py-2 text-xs"><ShieldCheck size={15} className="inline mr-2" />La contraseña MQTT se muestra una sola vez. Copia o descarga el paquete antes de salir.</div>

          <div className="grid grid-cols-1 lg:grid-cols-[1.05fr_0.8fr_1.65fr] gap-3 mt-3">
            <section className="rounded-md border border-line bg-page/50 p-3">
              <h3 className="text-sm font-bold mb-2">Cliente y acceso</h3>
              <dl className="grid grid-cols-2 gap-x-3 gap-y-2">
                <DeliveryField label="Cliente" value={handoff.customer.tenant_name} />
                <DeliveryField label="Parcela" value={handoff.customer.field_name} />
                <DeliveryField label="Correo" value={handoff.desktop.email || "Cuenta existente"} className="col-span-2" />
                <DeliveryField label="Contraseña inicial" value={handoff.desktop.initial_password} className="col-span-2" mono />
                <DeliveryField label="Tenant ID" value={handoff.customer.tenant_id} className="col-span-2" mono />
                <DeliveryField label="Field ID" value={handoff.customer.field_id} className="col-span-2" mono />
                <DeliveryField label="Desktop" value={handoff.desktop.url} className="col-span-2" mono />
              </dl>
            </section>

            <section className="rounded-md border border-line bg-page/50 p-3">
              <h3 className="text-sm font-bold mb-2">Dispositivo</h3>
              <dl className="grid grid-cols-2 gap-x-3 gap-y-2">
                <DeliveryField label="Device ID" value={handoff.device.id} className="col-span-2" mono />
                <DeliveryField label="Número de serie" value={handoff.device.serial_number} />
                <DeliveryField label="Estado" value={handoff.device.lifecycle_state} />
                <DeliveryField label="Tenant ID" value={handoff.device.tenant_id} className="col-span-2" mono />
                <DeliveryField label="Field ID" value={handoff.device.field_id} className="col-span-2" mono />
              </dl>
            </section>

            <section className="rounded-md border border-line bg-page/50 p-3">
              <h3 className="text-sm font-bold mb-2">Conexión MQTT</h3>
              <dl className="grid grid-cols-2 gap-x-3 gap-y-2">
                <DeliveryField label="Broker" value={handoff.mqtt.broker_host} mono />
                <DeliveryField label="Puerto" value={handoff.mqtt.broker_port} />
                <DeliveryField label="Usuario" value={handoff.mqtt.mqtt_username} className="col-span-2" mono />
                <DeliveryField label="Contraseña" value={handoff.mqtt.mqtt_password} className="col-span-2" mono />
                <DeliveryField label="Credencial" value={handoff.mqtt.credential_status} className="col-span-2" />
                <DeliveryField label="Telemetría" value={handoff.mqtt.telemetry_topic} className="col-span-2" mono />
                <DeliveryField label="Estado" value={handoff.mqtt.status_topic} className="col-span-2" mono />
                <DeliveryField label="Eventos" value={handoff.mqtt.events_topic} className="col-span-2" mono />
              </dl>
            </section>
          </div>

          <div className="flex flex-wrap justify-between gap-3 mt-3"><button onClick={reset} className="h-10 px-4 rounded-md border border-line font-semibold inline-flex items-center gap-2"><Plus size={16} />Nuevo onboarding</button><div className="flex gap-2"><button onClick={copyHandoff} className="h-10 px-4 rounded-md border border-line font-semibold inline-flex items-center gap-2"><Clipboard size={16} />Copiar</button><button onClick={downloadHandoff} className="h-10 px-4 rounded-md bg-brand text-white font-semibold inline-flex items-center gap-2"><Download size={16} />Descargar JSON</button></div></div>
        </Panel>
      )}
    </div>
  );
}
